/* @HEADER
 * @version 1.1.855 | 2026-10-24
 * @purpose Describe the role of AppShell in this repository.
 * @sidecar AppShell.tsx.header.md
 * @layer app | @hex _none_ | @ctx _none_
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-7887, ZVX-DEV-7734, ZVX-DEV-7685, ZVX-DEV-7422, ZVX-DEV-7414, ZVX-DEV-7233, ZVX-DEV-6986

// SpecRefs: ZVX-DEV-6409, ZVX-DEV-6373, ZVX-DEV-6297, ZVX-DEV-6269, ZVX-DEV-6253, ZVX-DEV-6225, ZVX-DEV-6103, ZVX-DEV-6090, ZVX-DEV-6083, ZVX-DEV-5920, ZVX-DEV-5432, ZVX-DEV-5092, ZVX-DEV-145, ZVX-DEV-1217, ZVX-CANVAS-007, ZVX-DEV-2166-SWITCHER, ZVX-DEV-2339, ZVX-DEV-2533

import {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useRef,
  lazy,
  Suspense,
} from 'react';
import type { ReactNode } from 'react';
import type { Route } from './routes';
import { isProjectScopedRoute } from './routes';
import { SpaceOverviewActionsContext } from './SpaceOverviewActionsContext';
import { ZvenixLogo } from './ZvenixLogo';
// ZVX-DEV-2853: the 2-axis connectivity dot relocated from a fixed top-right
// App.tsx overlay into the sidebar header, above the brand (see
// features/connectivity/README.md for the dot/panel contract itself --
// unchanged by this move, only its mount point and the panel's positioning
// strategy changed).
import { ConnectivityIndicator } from '../features/connectivity/public-api';
import { HelpMenu } from '../features/help/HelpMenu';
import { useMessages } from '../shared/i18n/useMessages';
import { getActiveProject, getAllProjects } from '../lib/projectStore';
import { SpaceNamesakeMount } from '../features/space-namesake/SpaceNamesakeMount';
import {
  createDirectionManagement,
  createIndexedDbDirectionManagementPort,
  DirectionsSection,
} from '../features/direction-management/public-api';
import {
  SpaceSettingsModal,
  SpaceVisibilitySection,
  createSpaceVisibilityPort,
  type SpaceSettingsSection,
  type SpaceSettingsTarget,
  type SpaceVisibilityPort,
} from '../features/space-settings/public-api';
import { getAllBoardGroupsIn, saveBoardGroupIn } from '../lib/db/boardData';
import { resolveDbName } from '../lib/db/dbName';
import { ViewerReadOnlyNotice } from '../features/auth/ViewerReadOnlyNotice';
import { LastSessionNotice } from '../features/feedback/LastSessionNotice'; // ZVX-DEV-7887
import { GlobalSearchWidget } from '../features/global-search/public-api';
import { AI_CHAT_IDS } from '../features/ai-chat/public-api';
// Lazy-loaded (ZVX-DEV-2938): AIChatPanel pulls in @wllama/wllama's embedded
// WASM runtime (~3.7MB) via BackendSelector -> WllamaBackend, which otherwise
// lands in the eager first-load bundle even though the panel only renders
// after the user explicitly toggles it open (aiChatEnabled && aiOpen
// below). Deferring the import keeps that weight out of the critical
// first-paint path entirely instead of just moving it to a separate
// always-fetched chunk.
const AIChatPanel = lazy(() =>
  import('../features/ai-chat/public-api').then((m) => ({ default: m.AIChatPanel })),
);
import {
  isAiChatEnabled,
  isDevNavVisible,
  isTranscriberEnabled,
} from '../shared/featureFlags';
import { useTheme } from '../shared/hooks/useTheme';
import { AssistantWindow } from '../features/assistant/AssistantWindow';
import { useAssistantShellSkills } from '../features/assistant/useAssistantShellSkills';
import { PaletteMount } from './PaletteMount';
import { usePaletteController } from './usePaletteController';
import type { BoardMode } from '@zvenix/tasks';
import { isFeatureVisible, setCommentAuthorName } from '@zvenix/tasks';
import {
  KanbanIcon,
  CanvasIcon,
  StructureIcon,
  WikiIcon,
  SettingsIcon,
  SearchIcon,
  AnalyticsIcon,
  EventsIcon,
  DiaryIcon,
  MicIcon,
  OkrIcon,
} from './NavIcons';
import { useRecordingContext } from '../shared/audio/RecordingContext';
import { EVENTS_TESTIDS } from '../features/events/events.testids';
import { DIARY_TESTIDS } from '../features/diary/diary.testids';
import { CANVAS_TESTIDS } from '@zvenix/kanvas';
import { WIKI_TESTIDS } from '@zvenix/wiki';
import { PBS_TESTIDS } from '../features/structure/structure.testids';
import { OKR_TESTIDS } from '../features/okr/okr.testids';
import { APP_SHELL_TESTIDS } from './app-shell.testids';
import { UserMenu } from '../features/account/public-api';
import { WorkspaceSwitcher } from '../features/workspace/public-api';
import {
  EntitlementUpsellPlacard,
  ENTITLEMENT_TESTIDS,
  EntitlementLifecycleBanner,
} from '../features/entitlement/public-api';
import { useEntitlementGate } from './useEntitlementGate';
import { useOrganisationScope } from './useOrganisationScope';
import { SHARED_WITH_ME_TESTIDS } from '../features/content-access/shared-with-me.testids';
import { ProjectSwitcher } from './ProjectSwitcher';
import type { BoardGroupRecord } from '../lib/db';
import { readAccountIdentity } from '../features/account/accountIdentity';
import { useSelfAvatarSync } from '../features/account/useSelfAvatarSync';
import { publishCommentAuthorName } from '../features/account/publishCommentAuthorName';
import { BoardNavEmptyState } from './BoardNavEmptyState';
import { OPEN_PROJECT_CREATE_EVENT } from './ProjectSwitcher';
import { useIsMobile } from '../hooks/useIsMobile';
import { mobileSelectors } from '../ui/mobile-selectors';
import { BottomSheet } from '../ui/BottomSheet';
import { MobileNavDrawer } from '../ui/MobileNavDrawer';
import { MobileBottomBar } from '@zvenix/tasks';
import type { MobileTabId } from '@zvenix/tasks';
import './BoardNav.css';
import './ProjectSwitcher.css';
import './AppShell.css';

interface NavItem {
  route: Route;
  label: string;
  icon: React.ReactNode;
  testId?: string;
  /** When set the item is only rendered when isFeatureVisible(featureKey, boardMode) is true. */
  featureKey?: import('@zvenix/tasks').BoardFeatureKey;
  /**
   * Временно скрыто из меню для реальных юзеров; dev включает
   * localStorage zvenix.devNav=1; cleanup-trigger: когда фичи готовы к показу.
   */
  devNavOnly?: boolean;
}

interface AppShellProps {
  currentRoute: Route;
  onNavigate: (route: Route) => void;
  children: ReactNode;
  /**
   * ZVX-DEV-6083 — a hash overlay (#organization, #account) is on screen. Those
   * pages keep `currentRoute` at 'tasks' by design (ZVX-DEV-2616), so without
   * this the ZVX-DEV-5483 guard read them as a project screen and blanked the
   * organization settings for a fresh Owner in an empty organization (e2e act I
   * on b8d81456a4, fleet #14985). Like Settings, they belong to the person.
   */
  hashOverlay?: boolean;
  boardGroups?: BoardGroupRecord[];
  activeBoardGroupId?: string | null;
  onSelectBoardGroup?: (groupId: string) => void;
  onCreateBoardGroup?: (name: string, parentId: string | null) => void;
  /**
   * ZVX-DEV-5092 — offered from the left-nav empty state when no project of
   * the active organization is visible. Optional: a host that has nowhere to
   * create simply renders the explanation without an action, which is honest,
   * where a dead button would not be.
   */
  onCreateProjectInActiveOrg?: () => void;
  onDeleteBoardGroup?: (groupId: string) => void;
  activeView?: import('./routes').SpaceView;
  onSwitchView?: (view: import('./routes').SpaceView) => void;
  canGoBack?: boolean;
  onGoBack?: () => void;
  /** Ref set by App to a scrollTop value that should be restored after back navigation. */
  pendingScrollRef?: React.RefObject<number | null>;
  /** ZVX-DEV-1217: App-wide UI tier — controls per-item sidebar gate (Expert vs Simple). */
  boardMode?: BoardMode;
  /**
   * ZVX-DEV-2166-CABINET-SHELL: open the personal cabinet (ЛК) from the
   * app-shell UserMenu. The host navigates to the #account route. Optional —
   * when omitted the menu still renders identity + sign-out but the "open
   * cabinet" item is hidden.
   */
  onOpenAccount?: () => void;
}

export function AppShell({
  currentRoute,
  onNavigate,
  children,
  boardGroups = [],
  activeBoardGroupId = null,
  onSelectBoardGroup,
  onCreateBoardGroup,
  onCreateProjectInActiveOrg,
  onDeleteBoardGroup,
  activeView,
  onSwitchView,
  canGoBack = false,
  onGoBack,
  pendingScrollRef,
  boardMode = 'expert',
  onOpenAccount,
  hashOverlay = false,
}: AppShellProps) {
  const m = useMessages();
  const directionManagement = useMemo(
    () => createDirectionManagement(createIndexedDbDirectionManagementPort()),
    [],
  );
  const [spaceSettingsTarget, setSpaceSettingsTarget] = useState<SpaceSettingsTarget | null>(null);
  // ZVX-DEV-6751 — the «Видимость» section's storage, wired here because the
  // app layer is what knows WHICH project's database a target names. Both
  // halves are ADDRESSED (`…In(dbName, …)`): the modal can be opened for a
  // Space in a project that is not the active one, and an ambient write would
  // land wherever the screen happens to be.
  //
  // The write hands `undefined` for «off» deliberately, the same shape the
  // board panel produces. Making that explicit is `saveBoardGroupInDb`'s job
  // (`expressSpaceOffStateExplicitly`, ZVX-DEV-6748) — writing around that
  // funnel would give the setting back its one-way defect, travelling as ON
  // and never as OFF.
  const spaceVisibilityPort = useMemo<SpaceVisibilityPort>(
    () =>
      createSpaceVisibilityPort({
        resolveDbNameForProject: (projectId) => {
          const project = getAllProjects().find((candidate) => candidate.id === projectId);
          return project ? resolveDbName(project) : null;
        },
        readGroupsIn: getAllBoardGroupsIn,
        saveGroupIn: saveBoardGroupIn,
      }),
    [],
  );
  const spaceSettingsSections = useMemo<readonly SpaceSettingsSection[]>(
    () => [
      {
        id: 'directions',
        label: m.spaceSettings.directions,
        render: ({ target }) => <DirectionsSection service={directionManagement} target={target} />,
      },
      {
        id: 'visibility',
        label: m.spaceSettings.visibility,
        render: ({ target }) => (
          <SpaceVisibilitySection port={spaceVisibilityPort} target={target} />
        ),
      },
    ],
    [
      directionManagement,
      m.spaceSettings.directions,
      m.spaceSettings.visibility,
      spaceVisibilityPort,
    ],
  );
  const spaceSettingsName = spaceSettingsTarget
    ? (boardGroups.find((group) => group.id === spaceSettingsTarget.spaceId)?.name ??
      spaceSettingsTarget.spaceId)
    : '';
  // ZVX-DEV-6409 — the title names the project too; by the TARGET's projectId,
  // not the active project, so it stays right if the two ever differ.
  const spaceSettingsProjectName = spaceSettingsTarget
    ? getAllProjects().find((project) => project.id === spaceSettingsTarget.projectId)?.name
    : undefined;

  /**
   * ZVX-DEV-5421 — tell the board who is at this device, so a comment is signed
   * with a name instead of the literal «user».
   *
   * Re-run on the display-name event as well as on mount: the cabinet lets a
   * person change their name without a reload (that event exists for exactly
   * that), and a board left holding the previous name would sign the next
   * comment with it.
   */
  useEffect(
    () =>
      publishCommentAuthorName({
        readDisplayName: () => readAccountIdentity()?.displayName,
        setCommentAuthorName,
        addEventListener: (type, l) => window.addEventListener(type, l),
        removeEventListener: (type, l) => window.removeEventListener(type, l),
      }),
    [],
  );
  // ZVX-DEV-5426 — pull this person's own photo once a session exists. The
  // publish half has existed since ZVX-DEV-4443 and the server's GET since the
  // same day; nothing had ever called it, so a photo set on one device reached
  // no other.
  useSelfAvatarSync();
  // Initialize theme on mount — reads localStorage['zvenix:theme'] and applies
  // data-theme attribute to <html> via useEffect. Without this, dark mode
  // only activates after opening Settings modal once. (DEMO-024-F regression.)
  useTheme();
  // ZVX-DEV-6766 -- the shell offers the skills IT owns (theme, locale) while
  // it is mounted. The canvas offers the grid while IT is mounted; nothing
  // here reaches into it.
  useAssistantShellSkills();
  const mainRef = useRef<HTMLElement>(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('zvenix:sidebar') === 'collapsed',
  );
  const [hasMic, setHasMic] = useState(false);
  const { isRecording, startRecording, stopRecording } = useRecordingContext();

  // Check mic availability once on mount
  useEffect(() => {
    navigator.mediaDevices
      ?.enumerateDevices?.()
      .then((devs) => setHasMic(devs.some((d) => d.kind === 'audioinput')))
      .catch(() => setHasMic(false));
  }, []);
  // Organisation scope (ZVX-DEV-5003, ZVX-DEV-5020, ZVX-DEV-3620) lives in its hook; asked before the palette, which it is handed to (ZVX-DEV-8233).
  const { outOfScope, hasOrganisation, personalOrganisation } = useOrganisationScope();
  // ZVX-DEV-7414 — the command palette's state, data, Ctrl+K and result
  // handling live in usePaletteController; the shell only mounts it.
  const palette = usePaletteController({ onNavigate, activeBoardGroupId: activeBoardGroupId ?? null, boardGroups, scopedOut: outOfScope });

  function toggleSidebar() {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('zvenix:sidebar', next ? 'collapsed' : 'expanded');
  }

  // ── Listen for sidebar collapse from focus mode toggle ──
  useEffect(() => {
    function handleSidebarToggle(e: Event) {
      const { collapsed: want } = (e as CustomEvent<{ collapsed: boolean }>).detail;
      setCollapsed(want);
    }
    window.addEventListener('zvenix:sidebar-toggle', handleSidebarToggle);
    return () => window.removeEventListener('zvenix:sidebar-toggle', handleSidebarToggle);
  }, []);

  // ── Global hotkey: Alt+Left (Ctrl+K is the palette's, in usePaletteController) ──
  useEffect(() => {
    function handleGlobalKeyDown(e: KeyboardEvent) {
      // Alt+Left — go back (like browser back)
      if (e.altKey && e.key === 'ArrowLeft' && canGoBack && onGoBack) {
        e.preventDefault();
        onGoBack();
      }
    }
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [canGoBack, onGoBack]);

  // ── Scroll restoration after back navigation ──
  useLayoutEffect(() => {
    if (pendingScrollRef && pendingScrollRef.current !== null && mainRef.current) {
      mainRef.current.scrollTop = pendingScrollRef.current;
      pendingScrollRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRoute, activeBoardGroupId, activeView]);

  // The shared viewer belongs to its own route and unmounts on navigation.
  const navigate = onNavigate;

  const isMobile = useIsMobile();
  const mobileActiveTab: MobileTabId = currentRoute === 'canvas' ? 'canvas' : 'tasks';
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  // ZVX-DEV-2531: mobileProjectOpen / mobileMenuOpen sheets are retired — the
  // project-switcher chip and [⋮] button now open the left nav-drawer below.
  const [mobileNavDrawerOpen, setMobileNavDrawerOpen] = useState(false);
  const [fabHidden, setFabHidden] = useState(false);
  const lastScrollTopRef = useRef<number>(0);

  // ── FAB auto-hide on scroll (ZVX-DEV-2339) ──
  // Attaches to the .content element (main scrollable area).
  useEffect(() => {
    if (!isMobile) return;
    const contentEl = mainRef.current;
    if (!contentEl) return;

    function handleScroll() {
      const scrollTop = contentEl!.scrollTop;
      const delta = scrollTop - lastScrollTopRef.current;
      lastScrollTopRef.current = scrollTop;
      // Hide on scroll down (delta > 4px), show on scroll up (delta < -4px).
      if (delta > 4) setFabHidden(true);
      else if (delta < -4) setFabHidden(false);
    }

    contentEl.addEventListener('scroll', handleScroll, { passive: true });
    return () => contentEl.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  // ── Mobile tab → route mapping (ZVX-DEV-2339; ZVX-DEV-2533: 3 tabs, Структура
  //    relocated into the [more] sheet) ──
  const handleMobileTabChange = useCallback(
    (tab: MobileTabId) => {
      if (tab === 'more') {
        setMobileMoreOpen(true);
      } else {
        if (tab === 'tasks') navigate('tasks');
        else if (tab === 'canvas') navigate('canvas');
      }
    },
    [navigate],
  );

  // Временно скрыто из меню для реальных юзеров; dev включает localStorage zvenix.devNav=1; cleanup-trigger: когда фичи готовы к показу.
  const devNavVisible = isDevNavVisible();

  // ZVX-DEV-6986 — read once per render, so the button and the panel below it
  // cannot disagree within one render about whether the assistant exists.
  const aiChatEnabled = isAiChatEnabled();

  const navItems: NavItem[] = [
    {
      route: 'events',
      label: m.nav.events,
      icon: <EventsIcon />,
      testId: EVENTS_TESTIDS.navItem,
      devNavOnly: true,
    },
    {
      route: 'diary',
      label: m.nav.diary,
      icon: <DiaryIcon />,
      testId: DIARY_TESTIDS.navItem,
      devNavOnly: true,
    },
    {
      route: 'analytics',
      label: m.nav.analytics,
      icon: <AnalyticsIcon />,
      featureKey: 'analyticsPage',
    },
    {
      route: 'okr',
      label: m.nav.okr,
      icon: <OkrIcon />,
      testId: OKR_TESTIDS.navItem,
      devNavOnly: true,
    },
  ];

  // ZVX-DEV-7422 — what the plan allows, both redirects, the space limit, the
  // read-only delete guard and the upsell placard live in useEntitlementGate;
  // the markup below reads the object it returns.
  const {
    canvasAllowed,
    wikiAllowed,
    crossProjectAllowed,
    gatedNavItems,
    anyToolHidden,
    isReadOnly,
    canCreateSpace,
    guardedDeleteBoardGroup,
    upsell,
  } = useEntitlementGate({
    navItems,
    boardGroups,
    currentRoute,
    activeView,
    onNavigate,
    onSwitchView,
    onDeleteBoardGroup,
  });

  return (
    <div className="app-shell">
      {/* ZVX-DEV-7174 — the floating «?» only on the phone, which has no
          sidebar wordmark and so no help menu; the desktop's way in is
          «ИИ-ассистент» in that menu. */}
      <AssistantWindow showOpener={isMobile} />
      {/* ZVX-DEV-6833 — this rail is dark in BOTH themes (--color-sidebar-bg),
          so anything that can float over it has to swap palettes rather than
          follow data-theme. The attribute IS that declaration; the draggable
          feedback button reads it by attribute so it never has to know this
          component's class names. The mobile drawer deliberately does not
          carry it — that one is light (see mobile.css). */}
      <nav
        className="sidebar"
        aria-label={m.nav.mainNavigation}
        data-collapsed={collapsed}
        data-chrome-surface="dark"
      >
        <div className="sidebar-header">
          {/* ZVX-DEV-2853: dot renders first -- above the logo (collapsed) or
              above the "ZveniX" wordmark (expanded). Sits outside the
              collapsed/expanded branch below since it appears in both. */}
          <ConnectivityIndicator />
          {/* ZVX-DEV-7158 — the wordmark (the logo on the rail) IS the help
              menu's trigger: the operator asked for no separate button, and
              before this it was a plain div that did nothing on a press, so no
              behaviour is displaced. The card opens beside the panel and covers
              nothing of it (ZVX-DEV-7734). */}
          {collapsed ? (
            <HelpMenu
              messages={m.help}
              triggerClassName="sidebar-logo-icon"
              triggerTitle={m.help.brandLabel}
            >
              <ZvenixLogo size={22} />
            </HelpMenu>
          ) : (
            <>
              <HelpMenu messages={m.help} triggerClassName="sidebar-brand">
                ZveniX
              </HelpMenu>
              {/* ZVX-DEV-7734 — organisation above the project; the collapsed rail keeps it in the footer. */}
              <WorkspaceSwitcher collapsed={false} />
              <ProjectSwitcher defaultName={m.app.title} />
            </>
          )}
          {/* ZVX-DEV-2530: the header entitlement pill (S13/ZVX-DEV-2470) was
              removed here — the company/licensee name is already shown by the
              WorkspaceSwitcher in the sidebar footer, so the top pill was
              redundant and visually noisy. Grace/read-only state stays visible
              via EntitlementLifecycleBanner; free-tier via the upsell nav item.
              The compact badge remains in the mobile header only. */}
        </div>
        {collapsed ? (
          <button
            type="button"
            className="sidebar-search-btn"
            onClick={() => setCollapsed(false)}
            aria-label={m.nav.search}
            title={m.nav.searchExpand}
          >
            <SearchIcon />
          </button>
        ) : (
          <GlobalSearchWidget onNavigate={onNavigate} scopedOut={outOfScope} />
        )}
        <ul className="sidebar-nav" role="list">
          {/* Compact destinations open their full-width overview in the main region. */}
          <li>
            <button className={`nav-item${currentRoute === 'tasks' ? ' active' : ''}`}
              data-testid={APP_SHELL_TESTIDS.navTasksButton}
              onClick={() => navigate('tasks')}
              aria-current={currentRoute === 'tasks' ? 'page' : undefined}
              title={collapsed ? m.nav.kanban : undefined}>
              <span className="nav-item__icon"><KanbanIcon /></span>
              {!collapsed && <span className="nav-item__label">{m.nav.kanban}</span>}
            </button>
          </li>
          {canvasAllowed && (
            <li>
              <button className={`nav-item${currentRoute === 'canvas' ? ' active' : ''}`}
                data-testid={CANVAS_TESTIDS.sidebar.entry}
                onClick={() => navigate('canvas')}
                aria-current={currentRoute === 'canvas' ? 'page' : undefined}
                title={collapsed ? m.canvas.sidebarLabel : undefined}>
                <span className="nav-item__icon"><CanvasIcon /></span>
                {!collapsed && <span className="nav-item__label">{m.canvas.sidebarLabel}</span>}
              </button>
            </li>
          )}
          {devNavVisible && (
            <li>
              <button
                className={`nav-item${currentRoute === 'structure' ? ' active' : ''}`}
                data-testid={PBS_TESTIDS.navItem}
                onClick={() => navigate('structure')}
                aria-current={currentRoute === 'structure' ? 'page' : undefined}
                title={collapsed ? m.structure.sidebarLabel : undefined}
              >
                <span className="nav-item__icon">
                  <StructureIcon />
                </span>
                {!collapsed && <span className="nav-item__label">{m.structure.sidebarLabel}</span>}
              </button>
            </li>
          )}
          {/* Wiki — Project-scoped page tree + editor, own-content-stream screen
              sibling of Канвас / Структура, reached from the sidebar (ZVX-DEV-1957).
              ZVX-DEV-2451: hidden entirely when `wiki` is absent from featureFlags. */}
          {wikiAllowed && (
            <li>
              <button className={`nav-item${currentRoute === 'wiki' ? ' active' : ''}`}
                data-testid={WIKI_TESTIDS.navEntry}
                onClick={() => navigate('wiki')}
                aria-current={currentRoute === 'wiki' ? 'page' : undefined}
                title={collapsed ? m.wiki.sidebarLabel : undefined}>
                <span className="nav-item__icon"><WikiIcon /></span>
                {!collapsed && <span className="nav-item__label">{m.wiki.sidebarLabel}</span>}
              </button>
            </li>
          )}
          {gatedNavItems
            .filter((item) => !item.featureKey || isFeatureVisible(item.featureKey, boardMode))
            .filter((item) => !item.devNavOnly || devNavVisible)
            .map((item) => (
              <li key={item.route}>
                <button
                  className={`nav-item${currentRoute === item.route ? ' active' : ''}`}
                  onClick={() => navigate(item.route)}
                  aria-current={currentRoute === item.route ? 'page' : undefined}
                  title={collapsed ? item.label : undefined}
                  {...(item.testId ? { 'data-testid': item.testId } : {})}
                >
                  <span className="nav-item__icon">{item.icon}</span>
                  {!collapsed && <span className="nav-item__label">{item.label}</span>}
                </button>
              </li>
            ))}
          {/* Borrowed content is now an addressable overview, not a sidebar tree. */}
          <li>
            <button className={`nav-item${currentRoute === 'shared-with-me' ? ' active' : ''}`}
              data-testid={SHARED_WITH_ME_TESTIDS.navEntry}
              onClick={() => navigate('shared-with-me')}
              aria-current={currentRoute === 'shared-with-me' ? 'page' : undefined}
              title={collapsed ? m.sharedWithMe.heading : undefined}>
              <span className="nav-item__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <circle cx="6" cy="12" r="2" /><circle cx="18" cy="6" r="2" /><circle cx="18" cy="18" r="2" />
                  <path d="m8 11 8-4M8 13l8 4" />
                </svg>
              </span>
              {!collapsed && <span className="nav-item__label">{m.sharedWithMe.heading}</span>}
            </button>
          </li>
          {/* ZVX-DEV-2451 (S4): single consistent upsell affordance replacing
              every tool the current entitlement hides — no per-tool bespoke
              copy in V1 (PRD §E2). Only rendered when >=1 tool is hidden. */}
          {anyToolHidden && (
            <li>
              <button
                className="nav-item nav-item--upsell"
                data-testid={ENTITLEMENT_TESTIDS.upsellNavItem}
                onClick={upsell.openFeature}
                title={collapsed ? m.entitlement.upsell.navItemLabel : undefined}
              >
                <span className="nav-item__icon" aria-hidden="true">
                  🔒
                </span>
                {!collapsed && (
                  <span className="nav-item__label">{m.entitlement.upsell.navItemLabel}</span>
                )}
              </button>
            </li>
          )}
        </ul>
        {aiChatEnabled && (
          <button
            className={`ai-chat-toggle${aiOpen ? ' ai-chat-toggle--active' : ''}`}
            onClick={() => setAiOpen((o) => !o)}
            data-testid={AI_CHAT_IDS.toggleButton}
          >
            {m.aiChat.toggle}
          </button>
        )}
        <div className="sidebar-footer">
          {/* ZVX-DEV-2166-SWITCHER: the workspace switcher — shows the active
              workspace name and lets the user switch, create, or open settings.
              Offline-first: anonymous sees «Локальное (офлайн)» and the app
              stays fully usable. Switching never clears local data. */}
          {collapsed && (
            <div className="sidebar-footer-row">
              <WorkspaceSwitcher collapsed />
            </div>
          )}
          {/* ZVX-DEV-2166-CABINET-SHELL: the canonical app-shell user menu —
              the single identity + sign-out surface. Offline-first: shows a
              «Войти» affordance when signed out and never gates the app. */}
          <div className="sidebar-footer-row">
            <UserMenu
              collapsed={collapsed}
              onOpenAccount={
                onOpenAccount ??
                (() => {
                  window.location.hash = '#account';
                })
              }
            />
          </div>
          {/* ZVX-DEV-2375: mic button only when transcriber is enabled (default OFF in prod). */}
          {hasMic && isTranscriberEnabled() && (
            <div className="sidebar-footer-row">
              <button
                type="button"
                className={`sidebar-footer-btn${isRecording ? ' sidebar-footer-btn--recording' : ''}`}
                onClick={isRecording ? stopRecording : startRecording}
                aria-label={isRecording ? m.nav.stopRecording : m.nav.startRecording}
                title={isRecording ? m.nav.stopRecording : m.nav.startRecording}
                data-testid="global-record-btn"
              >
                <span className="nav-item__icon">
                  <MicIcon />
                </span>
                {!collapsed && (
                  <span className="nav-item__label">
                    {isRecording ? m.nav.stopRecording : m.nav.startRecording}
                  </span>
                )}
              </button>
            </div>
          )}
          {/* ZVX-DEV-7158 — the round «?» that stood here (ZVX-DEV-6924) moved
              to the wordmark at the top of this sidebar: one menu, one way in.
              It is still the ONLY way into feedback, and it still has no screen
              corner to lose (ZVX-DEV-6832, ZVX-DEV-6481). */}
          <div className="sidebar-footer-row">
            <button
              type="button"
              className={`sidebar-footer-btn${currentRoute === 'settings' ? ' sidebar-footer-btn--active' : ''}`}
              onClick={() => navigate('settings')}
              aria-label={m.nav.settings}
              title={m.nav.settings}
              data-testid={APP_SHELL_TESTIDS.settingsOpenButton}
            >
              <span className="nav-item__icon">
                <SettingsIcon />
              </span>
              {!collapsed && <span className="nav-item__label">{m.nav.settings}</span>}
            </button>
            <button
              type="button"
              className="sidebar-collapse-btn"
              onClick={toggleSidebar}
              aria-label={collapsed ? m.nav.expand : m.nav.collapse}
              title={collapsed ? m.nav.expand : m.nav.collapse}
            >
              {collapsed ? '›' : '‹'}
            </button>
          </div>
        </div>
      </nav>

      {aiChatEnabled && aiOpen && (
        <Suspense
          fallback={<div className="ai-chat-panel ai-chat-panel--loading">{m.aiChat.loading}</div>}
        >
          <AIChatPanel onClose={() => setAiOpen(false)} />
        </Suspense>
      )}

      <PaletteMount palette={palette} currentSpaceActive={!!activeBoardGroupId} />

      {/* ZVX-DEV-2451 (S4): shared upsell placard, opened by the single
          locked-tools nav affordance above. */}
      {upsell.open && (
        <EntitlementUpsellPlacard
          onClose={upsell.close}
          onOpenAccount={
            onOpenAccount ??
            (() => {
              window.location.hash = '#account';
            })
          }
          title={upsell.title}
          body={upsell.body}
        />
      )}

      <div className="content-area">
        {/* ZVX-DEV-2469 (S12): grace/read-only lifecycle banner -- top of the
            app chrome, above both the desktop and mobile content so it is
            never missed regardless of the active view. Renders nothing when
            enforcement is off or the entitlement is active/none. */}
        <EntitlementLifecycleBanner
          onOpenAccount={
            onOpenAccount ??
            (() => {
              window.location.hash = '#account';
            })
          }
        />
        {/* ZVX-DEV-6253 (T1): the Viewer role reads — said once here, in the
            same place; quiet while the entitlement banner already says so. */}
        <ViewerReadOnlyNotice entitlementReadOnly={isReadOnly} />
        {/* ZVX-DEV-7887: the previous session ended while a page was in front of the person -- asked once, quietly, after sign-in. */}
        <LastSessionNotice />

        {/* ZVX-DEV-2339: Mobile compact header — 48px, only on mobile.
            Desktop header is the sidebar (unchanged above). */}
        {isMobile && (
          <header className="mobile-header" data-testid={mobileSelectors.mobileHeader}>
            {/* ZVX-DEV-2531: burger opens the left nav-drawer (project/space +
                account context) — one of three equivalent open triggers,
                alongside the project-switcher chip and [⋮] below. */}
            <button
              type="button"
              className="mobile-header__btn mobile-header__burger"
              aria-label={m.mobile.nav.drawer.label}
              data-testid={mobileSelectors.mobileHeaderBurger}
              onClick={() => setMobileNavDrawerOpen(true)}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <line x1="4" y1="7" x2="20" y2="7" />
                <line x1="4" y1="12" x2="20" y2="12" />
                <line x1="4" y1="17" x2="20" y2="17" />
              </svg>
            </button>

            {/* ZVX-DEV-2744 (BUG 3): the chip now opens the real project
                switcher (reuses the desktop ProjectSwitcher component rather
                than rebuilding one) instead of the left nav-drawer — the
                drawer already has its own dedicated burger trigger. */}
            <div
              className="mobile-header__project-switcher"
              data-testid={mobileSelectors.mobileProjectSwitcher}
            >
              <ProjectSwitcher
                defaultName={m.app.title}
                ariaLabel={m.mobile.header.projectSwitcher}
              />
            </div>

            {/* ZVX-DEV-2754: the persistent compact-dot entitlement badge was
                removed from the mobile header per the product owner's
                amendment (§2.7.6, ZVX-DEV-2753) -- it read as an
                (unclickable-for-that-purpose) avatar slot. The tier is now
                shown as a text label inside UserMenu's dropdown instead
                (desktop sidebar footer + mobile drawer footer), one tap
                deeper rather than always-on-screen. EntitlementStatusBadge
                itself is NOT deleted -- it stays available for other callers;
                only this render site is removed. */}

            <button
              type="button"
              className="mobile-header__btn"
              aria-label={m.mobile.header.addButton}
              data-testid={mobileSelectors.mobileAddButton}
              onClick={() => {
                /* FAB add action — dispatch the same custom event the FAB uses */
                window.dispatchEvent(new CustomEvent('mobile:add-card'));
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>

            {/* ZVX-DEV-2744 (BUG 3): [⋮] opens the "Ещё" tools sheet — a
                distinct action from the burger (drawer) and chip (project
                switcher). */}
            <button
              type="button"
              className="mobile-header__btn"
              aria-label={m.mobile.header.menuButton}
              data-testid={mobileSelectors.mobileMenuButton}
              onClick={() => setMobileMoreOpen(true)}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="5" r="1.5" fill="currentColor" stroke="none" />
                <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
                <circle cx="12" cy="19" r="1.5" fill="currentColor" stroke="none" />
              </svg>
            </button>
          </header>
        )}

        {/* ZVX-DEV-1975 (Card & Space Privacy P2 redesign): the per-space privacy
            control no longer lives in a dedicated top band here. It is now an
            icon-only control rendered inline to the LEFT of the active Space name
            inside the board-group header (App.tsx wires it through the kanban
            BoardGroupPage renderTitlePrefix slot). The former dedicated band is
            intentionally removed — only the main content remains. */}
        <SpaceOverviewActionsContext.Provider value={{
          onCreateSpace: onCreateBoardGroup,
          onDeleteSpace: guardedDeleteBoardGroup,
          onOpenSpaceSettings: (spaceId) => {
            const project = getActiveProject();
            if (!project) return;
            setSpaceSettingsTarget({ projectId: project.id, spaceId, initialSectionId: 'directions' });
          },
          canCreateSpace,
          onCreateBlocked: upsell.openSpaceLimit,
          crossProjectAllowed,
          hasOrganisation,
          personalOrganisation,
        }}>
        <main className="content" id="main-content" ref={mainRef}>
          {/* ZVX-DEV-5483 — the other half of ZVX-DEV-5003.
              That slice stopped the left nav listing a foreign project's
              Spaces as though they were this organization's, and left the
              screen BESIDE it drawing that project's boards, cards, canvases
              and wiki under the new organization's name. The operator's rule
              is that an empty organization reads as empty (ZVX-DEV-5473), and
              a board area is the largest thing on screen that can contradict
              it.
              Only project screens are replaced: Settings and the admin page
              belong to the person, not to a project, and blanking Settings
              would take away the switch that fixes the situation. Which is
              which is declared once, in routes.ts. */}
          {outOfScope && isProjectScopedRoute(currentRoute) && !hashOverlay ? (
            <BoardNavEmptyState
              title={m.projectSwitcher.otherOrgNote}
              hint={m.projectSwitcher.otherOrgHint}
              createLabel={m.projectSwitcher.otherOrgCreate}
              testIds={{
                root: APP_SHELL_TESTIDS.foreignProjectMain,
                create: APP_SHELL_TESTIDS.foreignProjectMainCreate,
              }}
              onCreate={
                onCreateProjectInActiveOrg ??
                (() => window.dispatchEvent(new CustomEvent(OPEN_PROJECT_CREATE_EVENT)))
              }
            />
          ) : (
            children
          )}
        </main>
        </SpaceOverviewActionsContext.Provider>
      </div>

      {/* ZVX-DEV-2339: Mobile bottom bar — only on mobile.
          Desktop sidebar stays unchanged. */}
      {isMobile && (
        <MobileBottomBar
          activeTab={mobileActiveTab}
          onTabChange={handleMobileTabChange}
          messages={m.mobile.nav}
          testIds={{
            bottomBar: mobileSelectors.bottomBar,
            bottomBarTab: mobileSelectors.bottomBarTab,
          }}
        />
      )}

      {/* ZVX-DEV-2339: FAB — floating add button, mobile only */}
      {isMobile && (
        <button
          type="button"
          className={`mobile-fab${fabHidden ? ' mobile-fab--hidden' : ''}`}
          aria-label={m.mobile.fab.add}
          data-testid={mobileSelectors.fab}
          onClick={() => window.dispatchEvent(new CustomEvent('mobile:add-card'))}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>
      )}

      {/* ZVX-DEV-2339 / ZVX-DEV-2533 / ZVX-DEV-2531: [Ещё] bottom-sheet —
          destination-tool picker only. Restyled from bordered chip-buttons to
          a clean, unbordered, full-width list (icon + label), each row
          >= 44px touch target. Структура/События/Дневник relocated here
          after leaving the tab bar (no destination becomes unreachable).
          Настройки is retired from this list (ZVX-DEV-2531) — the left
          nav-drawer below is now the single owner of Settings on mobile. */}
      {isMobile && (
        <BottomSheet open={mobileMoreOpen} onClose={() => setMobileMoreOpen(false)}>
          <div className="mobile-more-sheet">
            <h3 className="mobile-more-sheet__title">{m.mobile.header.moreSheetTitle}</h3>
            <ul className="mobile-more-sheet__list" role="list">
              <li>
                <button
                  type="button"
                  className="mobile-more-sheet__row"
                  data-testid={mobileSelectors.moreSheetItem('structure')}
                  onClick={() => {
                    setMobileMoreOpen(false);
                    navigate('structure');
                  }}
                >
                  <span className="mobile-more-sheet__row-icon" aria-hidden="true">
                    <StructureIcon />
                  </span>
                  <span className="mobile-more-sheet__row-label">{m.structure.sidebarLabel}</span>
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className="mobile-more-sheet__row"
                  data-testid={mobileSelectors.moreSheetItem('wiki')}
                  onClick={() => {
                    setMobileMoreOpen(false);
                    navigate('wiki');
                  }}
                >
                  <span className="mobile-more-sheet__row-icon" aria-hidden="true">
                    <WikiIcon />
                  </span>
                  <span className="mobile-more-sheet__row-label">{m.wiki.sidebarLabel}</span>
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className="mobile-more-sheet__row"
                  data-testid={mobileSelectors.moreSheetItem('events')}
                  onClick={() => {
                    setMobileMoreOpen(false);
                    navigate('events');
                  }}
                >
                  <span className="mobile-more-sheet__row-icon" aria-hidden="true">
                    <EventsIcon />
                  </span>
                  <span className="mobile-more-sheet__row-label">{m.nav.events}</span>
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className="mobile-more-sheet__row"
                  data-testid={mobileSelectors.moreSheetItem('diary')}
                  onClick={() => {
                    setMobileMoreOpen(false);
                    navigate('diary');
                  }}
                >
                  <span className="mobile-more-sheet__row-icon" aria-hidden="true">
                    <DiaryIcon />
                  </span>
                  <span className="mobile-more-sheet__row-label">{m.nav.diary}</span>
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className="mobile-more-sheet__row"
                  data-testid={mobileSelectors.moreSheetItem('analytics')}
                  onClick={() => {
                    setMobileMoreOpen(false);
                    navigate('analytics');
                  }}
                >
                  <span className="mobile-more-sheet__row-icon" aria-hidden="true">
                    <AnalyticsIcon />
                  </span>
                  <span className="mobile-more-sheet__row-label">{m.nav.analytics}</span>
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className="mobile-more-sheet__row"
                  data-testid={mobileSelectors.moreSheetItem('okr')}
                  onClick={() => {
                    setMobileMoreOpen(false);
                    navigate('okr');
                  }}
                >
                  <span className="mobile-more-sheet__row-icon" aria-hidden="true">
                    <OkrIcon />
                  </span>
                  <span className="mobile-more-sheet__row-label">{m.nav.okr}</span>
                </button>
              </li>
              {/* ZVX-DEV-2531: Настройки retired from [Ещё] — the left
                  nav-drawer is now the single owner of Settings on mobile
                  (see MobileNavDrawer footer below). */}
            </ul>
          </div>
        </BottomSheet>
      )}

      {/* ZVX-DEV-2531: left nav-drawer — project/space switcher + Настройки /
          workspace switcher / account footer. Replaces the retired
          mobileProjectOpen and mobileMenuOpen sheets above; opened by the
          burger, project-switcher chip, [⋮], or left-edge swipe-right; closed
          by scrim tap, swipe-left, or Escape/back. Carries no tool-destination
          links (those stay exclusively in [Ещё] above — zero overlap). */}
      <MobileNavDrawer
        open={mobileNavDrawerOpen}
        onOpen={() => setMobileNavDrawerOpen(true)}
        onClose={() => setMobileNavDrawerOpen(false)}
        enabled={isMobile}
        boardGroups={boardGroups}
        activeBoardGroupId={activeBoardGroupId}
        onSelectBoardGroup={onSelectBoardGroup}
        onCreateBoardGroup={onCreateBoardGroup}
        onDeleteBoardGroup={guardedDeleteBoardGroup ?? onDeleteBoardGroup}
        canCreateSpace={canCreateSpace}
        onSpaceLimitBlocked={upsell.openSpaceLimit}
        onNavigateSettings={() => navigate('settings')}
        currentRoute={currentRoute}
        onOpenAccount={
          onOpenAccount ??
          (() => {
            window.location.hash = '#account';
          })
        }
      />

      {spaceSettingsTarget && (
        <SpaceSettingsModal
          open
          spaceName={spaceSettingsName}
          projectName={spaceSettingsProjectName}
          target={spaceSettingsTarget}
          sections={spaceSettingsSections}
          onClose={() => setSpaceSettingsTarget(null)}
        />
      )}

      {/* ZVX-DEV-5366 -- the namesake question. Mounted here rather than in
          App.tsx because it needs nothing from the board screen: it listens for
          a registry sync, and renders nothing at all until there is something
          to ask. Measured by opusUI on 2026-08-31: the machinery it drives
          (ZVX-DEV-5315/5318/5346) had no caller anywhere, so the operator's twin
          Spaces -- which carry DIFFERENT server ids and are therefore beyond
          every automatic rule -- could never be reconciled by anything. */}
      <SpaceNamesakeMount />
    </div>
  );
}
